import { User, Building2, DollarSign, Calendar, FileText, Code } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

interface Variable {
  key: string;
  label: string;
  example: string;
  category: string;
}

const variables: Variable[] = [
  // Contact
  { key: '{{contact.name}}', label: 'Contactnaam', example: 'Jan de Vries', category: 'Contact' },
  { key: '{{contact.email}}', label: 'E-mail', example: 'jan@example.nl', category: 'Contact' },
  { key: '{{contact.phone}}', label: 'Telefoon', example: '+31 6 12345678', category: 'Contact' },
  { key: '{{contact.company}}', label: 'Bedrijf', example: 'Acme B.V.', category: 'Contact' },
  
  // Company
  { key: '{{company.name}}', label: 'Bedrijfsnaam', example: 'Uw Bedrijf B.V.', category: 'Bedrijf' },
  { key: '{{company.address}}', label: 'Adres', example: 'Hoofdstraat 1', category: 'Bedrijf' },
  { key: '{{company.zip}}', label: 'Postcode', example: '1234 AB', category: 'Bedrijf' },
  { key: '{{company.city}}', label: 'Plaats', example: 'Amsterdam', category: 'Bedrijf' },
  { key: '{{company.phone}}', label: 'Telefoon', example: '+31 20 1234567', category: 'Bedrijf' },
  { key: '{{company.email}}', label: 'E-mail', example: 'info@uwbedrijf.nl', category: 'Bedrijf' },
  { key: '{{company.kvk}}', label: 'KvK-nummer', example: '12345678', category: 'Bedrijf' },
  { key: '{{company.btw}}', label: 'BTW-nummer', example: 'NL123456789B01', category: 'Bedrijf' },
  
  // Deal
  { key: '{{deal.name}}', label: 'Dealnaam', example: 'Project XYZ', category: 'Deal' },
  { key: '{{deal.amount}}', label: 'Bedrag', example: '€10.000,00', category: 'Deal' },
  { key: '{{deal.stage}}', label: 'Fase', example: 'Offerte verstuurd', category: 'Deal' },
  { key: '{{deal.close_date}}', label: 'Sluitdatum', example: '31-12-2026', category: 'Deal' },
  
  // Quote
  { key: '{{quote.number}}', label: 'Offertenummer', example: 'OFF-2026-001', category: 'Offerte' },
  { key: '{{quote.date}}', label: 'Offertedatum', example: '11-01-2026', category: 'Offerte' },
  { key: '{{quote.valid_until}}', label: 'Geldig tot', example: '11-02-2026', category: 'Offerte' },
  { key: '{{quote.total}}', label: 'Totaalbedrag', example: '€12.100,00', category: 'Offerte' },
  
  // Date
  { key: '{{date.today}}', label: 'Vandaag', example: '11-01-2026', category: 'Datum' },
  { key: '{{date.year}}', label: 'Jaar', example: '2026', category: 'Datum' },
];

interface VariablesMenuProps {
  onInsert: (tag: string, label: string) => void;
  buttonVariant?: 'default' | 'outline' | 'ghost';
}

export default function VariablesMenu({ onInsert, buttonVariant = 'outline' }: VariablesMenuProps) {
  const categories = [...new Set(variables.map((v) => v.category))];

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Contact':
        return <User className="h-4 w-4" />;
      case 'Bedrijf':
        return <Building2 className="h-4 w-4" />;
      case 'Deal':
        return <DollarSign className="h-4 w-4" />;
      case 'Offerte':
        return <FileText className="h-4 w-4" />;
      case 'Datum':
        return <Calendar className="h-4 w-4" />;
      default:
        return <Code className="h-4 w-4" />;
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={buttonVariant} size="sm">
          <Code className="h-4 w-4 mr-1" />
          Variabelen
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="max-h-[400px] w-[320px] overflow-y-auto">
        <div className="px-2 py-1.5">
          <p className="text-xs text-muted-foreground">
            Klik om een variabele in te voegen
          </p>
        </div>
        <DropdownMenuSeparator />

        {categories.map((category, categoryIndex) => (
          <div key={category}>
            {categoryIndex > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="text-xs text-muted-foreground font-semibold flex items-center gap-1.5">
              {getCategoryIcon(category)}
              {category}
            </DropdownMenuLabel>
            {variables
              .filter((v) => v.category === category)
              .map((variable) => (
                <DropdownMenuItem
                  key={variable.key}
                  onClick={() => onInsert(variable.key, variable.label)}
                  className="flex items-center justify-between py-1"
                >
                  <span className="text-sm">{variable.label}</span>
                  <Badge
                    variant="info"
                    className="h-[18px] text-[10px] px-1.5"
                  >
                    {variable.key}
                  </Badge>
                </DropdownMenuItem>
              ))}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
